import { useRegisterAgentContext } from '@backrs_ui/grow-ui/src/agentContext/useRegisterAgentContext';
import {
  BoosterPlan,
  ReportFacts,
} from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.types';

import { useReportFactsAgentContext } from './hooks/useReportFactsAgentContext';
import { buildPlanSummary } from './utils/planSummary';

/**
 * Register the output page with Lubav — the report facts, the plan summary a student can point at,
 * and its two visuals (the study plan's stats/schedule/per-day calendar and the test-day
 * projection) — so its turns cite the computed numbers rather than inventing them. Registers
 * nothing until a plan exists.
 */
export const useAgentContext = (reportFacts: ReportFacts | null, plan: BoosterPlan | null) => {
  useReportFactsAgentContext(reportFacts);

  // The prose the student actually reads at the top of the page, so a question about "this
  // summary" resolves to the same wording rather than a re-derivation.
  useRegisterAgentContext(
    plan
      ? {
          kind: 'section',
          title: 'Plan summary',
          description: buildPlanSummary(reportFacts?.test ?? null, plan),
        }
      : null,
  );

  useRegisterAgentContext(
    plan
      ? {
          kind: 'visual',
          label: 'ACT/SAT study plan',
          display:
            'Stat tiles (total weeks, hours per week, and points to gain when a target score resolved) beside a plan summary, above a month-view study calendar.',
          detail:
            'Ground any summary or discussion in these values; do not invent schedule details. The calendar lists every plan day by date with its focus, so a per-week breakdown or a question about a specific date can be answered from it.',
          data: {
            stats: plan.stats,
            startDate: plan.startDate,
            testDate: plan.testDate,
            studyProfile: plan.archetype,
            sectionStudyShare: plan.sections,
            calendar: plan.days.map((day) => ({
              date: day.date,
              week: day.week,
              kind: day.kind,
              focus:
                day.targets
                  .map((target) => target.skillText)
                  .filter(Boolean)
                  .join('; ') ||
                day.entries.map((entry) => entry.label).join(', ') ||
                undefined,
              note: day.note,
            })),
          },
        }
      : null,
  );

  useRegisterAgentContext(
    plan?.projection
      ? {
          kind: 'visual',
          label: 'Projected test-day score',
          display:
            'A projected composite with a ± confidence band, a "what if I added X hrs/week" slider, and a per-section current → projected breakdown.',
          detail:
            'An estimate from the schedule and entered scores, never a guarantee. Always cite the ± band with the number; the slider indexes a precomputed curve of extra weekly hours.',
          data: {
            current: plan.projection.current,
            composite: plan.projection.composite,
            ci95: plan.projection.ci95,
            weeksRemaining: plan.projection.weeksRemaining,
            weeklyHours: plan.projection.weeklyHours,
            sections: plan.projection.sections,
            whatIfCurve: plan.projection.curve,
          },
        }
      : null,
  );
};
